<template>
  <v-card outlined class="overflow-hidden">
    <v-row no-gutters style="min-height: 520px">
      <!-- LEFT: chart selector list -->
      <v-col
        cols="12"
        sm="4"
        md="3"
        class="grey lighten-5"
        style="border-right: 1px solid rgba(0, 0, 0, 0.1)"
      >
        <v-progress-linear v-if="loading" indeterminate color="primary" height="2" />
        <v-list nav class="pa-2">
          <v-subheader class="text-caption text-uppercase font-weight-bold px-2 mt-1">
            {{ $t("statistics") || "Statistics" }}
          </v-subheader>
          <v-list-item-group v-model="selectedChart" mandatory color="primary">
            <v-list-item
              v-for="c in chartDefs"
              :key="c.key"
              :value="c.key"
              class="mb-1 rounded"
            >
              <v-list-item-icon class="mr-3 my-3">
                <v-icon>{{ c.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">
                  {{ c.label }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption mt-1">
                  {{ c.stat }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="my-3">
                <v-icon small color="grey lighten-2">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-col>

      <!-- RIGHT: chart panel -->
      <v-col cols="12" sm="8" md="9" class="pa-4">
        <!-- loading skeleton -->
        <div
          v-if="loading"
          class="d-flex justify-center align-center"
          style="min-height: 460px"
        >
          <v-progress-circular indeterminate color="primary" size="48" />
        </div>

        <template v-else>
          <!-- Meals: horizontal stacked bar -->
          <template v-if="selectedChart === 'meals'">
            <apexchart
              v-if="mealCategories.length"
              type="bar"
              :height="Math.max(360, mealCategories.length * 34 + 80)"
              :options="mealsOpts"
              :series="mealsSeries"
            />
            <empty-chart v-else />
          </template>

          <!-- Shifts: donut -->
          <template v-else-if="selectedChart === 'shifts'">
            <apexchart
              v-if="shiftSeries.some((v) => v > 0)"
              type="donut"
              height="460"
              :options="shiftsOpts"
              :series="shiftSeries"
            />
            <empty-chart v-else />
          </template>

          <!-- Companies: horizontal stacked bar -->
          <template v-else-if="selectedChart === 'companies'">
            <apexchart
              v-if="companyCategories.length"
              type="bar"
              :height="Math.max(300, companyCategories.length * 44 + 80)"
              :options="companiesOpts"
              :series="companiesSeries"
            />
            <empty-chart v-else />
          </template>

          <!-- Daily: stacked bar -->
          <template v-else-if="selectedChart === 'days'">
            <apexchart
              v-if="hasDates && dayCategories.length"
              type="bar"
              height="460"
              :options="daysOpts"
              :series="daysSeries"
            />
            <div
              v-else
              class="d-flex flex-column justify-center align-center grey--text"
              style="min-height: 400px"
            >
              <v-icon size="48" color="grey lighten-2">mdi-calendar-range</v-icon>
              <span class="text-body-2 mt-3">{{ $t("no_data") }}</span>
              <span class="text-caption grey--text text--lighten-1 mt-1">
                Select a wider date range to see the daily trend
              </span>
            </div>
          </template>
        </template>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import reportService from "@/services/report";
import companyService from "@/services/company";

const SHIFT_COLORS = ["#1E88E5", "#43A047", "#FB8C00", "#8E24AA"];

const EmptyChart = {
  functional: true,
  render: (h, ctx) =>
    h(
      "div",
      {
        class: "d-flex justify-center align-center grey--text text--lighten-1",
        style: "min-height: 400px",
      },
      [h("span", { class: "text-body-2" }, ctx.parent.$t("no_data"))]
    ),
};

export default {
  name: "RestaurantGroupMealsStats",
  components: { apexchart: VueApexCharts, EmptyChart },
  props: {
    date: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      rawData: [],
      companies: [],
      selectedChart: "meals",
    };
  },
  computed: {
    totalOrders() {
      return this.rawData.reduce((s, i) => s + Number(i.quantity || 0), 0);
    },
    chartDefs() {
      return [
        {
          key: "meals",
          icon: "mdi-food-drumstick",
          label: this.$t("meal_in_meals"),
          stat: this.mealCategories.length
            ? `${this.mealCategories.length} ${this.$t("meal_meal") || "items"}`
            : "—",
        },
        {
          key: "shifts",
          icon: "mdi-clock-time-four-outline",
          label: this.$t("orders_by_shift"),
          stat: this.totalOrders
            ? `${this.totalOrders} ${this.$t("total") || "total"}`
            : "—",
        },
        {
          key: "days",
          icon: "mdi-calendar-range",
          label: this.$t("daily"),
          stat: this.dayCategories.length
            ? `${this.dayCategories.length} ${this.$t("days") || "days"}`
            : "—",
        },
        {
          key: "companies",
          icon: "mdi-office-building",
          label: this.$t("companies") || "Companies",
          stat: this.companyCategories.length
            ? `${this.companyCategories.length} ${this.$t("companies") || "companies"}`
            : "—",
        },
      ];
    },
    shiftLabels() {
      return [
        this.$t("1st_shift"),
        this.$t("2nd_shift"),
        this.$t("3rd_shift"),
        this.$t("4th_shift"),
      ];
    },

    // ─── Meals chart ──────────────────────────────────────────────────────────
    mealAgg() {
      const map = {};
      for (const item of this.rawData) {
        const n = item.meal_name;
        if (!map[n]) map[n] = [0, 0, 0, 0];
        const s = (item.shift || 1) - 1;
        if (s >= 0 && s < 4) map[n][s] += Number(item.quantity || 0);
      }
      return map;
    },
    mealCategories() {
      return Object.entries(this.mealAgg)
        .map(([name, shifts]) => ({ name, total: shifts.reduce((a, b) => a + b, 0) }))
        .sort((a, b) => b.total - a.total)
        .map((m) => m.name);
    },
    mealsSeries() {
      return this.shiftLabels.map((label, i) => ({
        name: label,
        data: this.mealCategories.map((n) => this.mealAgg[n]?.[i] || 0),
      }));
    },
    mealsOpts() {
      return {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
          fontFamily: "Roboto, sans-serif",
        },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 3, barHeight: "70%" },
        },
        colors: SHIFT_COLORS,
        xaxis: {
          categories: this.mealCategories,
          labels: { style: { fontSize: "12px" } },
        },
        yaxis: { labels: { style: { fontSize: "12px" }, maxWidth: 220 } },
        legend: { position: "bottom", horizontalAlign: "center" },
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false },
        fill: { opacity: 1 },
        grid: { borderColor: "#f1f1f1" },
      };
    },

    // ─── Shifts donut ─────────────────────────────────────────────────────────
    shiftSeries() {
      const totals = [0, 0, 0, 0];
      for (const item of this.rawData) {
        const s = (item.shift || 1) - 1;
        if (s >= 0 && s < 4) totals[s] += Number(item.quantity || 0);
      }
      return totals;
    },
    shiftsOpts() {
      return {
        chart: { type: "donut", fontFamily: "Roboto, sans-serif" },
        labels: this.shiftLabels,
        colors: SHIFT_COLORS,
        legend: { position: "bottom" },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${val.toFixed(1)}%`,
          dropShadow: { enabled: false },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "60%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: this.$t("total"),
                  formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0),
                },
              },
            },
          },
        },
        tooltip: { y: { formatter: (val) => `${val} ${this.$t("meal_meal")}` } },
      };
    },

    // ─── Companies chart ────────────────────────────────────────────────────
    companyNameMap() {
      const map = {};
      for (const c of this.companies) {
        map[c.id] = c.name;
        if (Array.isArray(c.children)) {
          for (const child of c.children) map[child.id] = child.name;
        }
      }
      return map;
    },
    companyAgg() {
      const map = {};
      for (const item of this.rawData) {
        const cid = item.company_id;
        if (!cid) continue;
        if (!map[cid]) map[cid] = [0, 0, 0, 0];
        const s = (item.shift || 1) - 1;
        if (s >= 0 && s < 4) map[cid][s] += Number(item.quantity || 0);
      }
      return map;
    },
    companyCategories() {
      return Object.entries(this.companyAgg)
        .map(([cid, shifts]) => ({
          cid,
          name: this.companyNameMap[cid] || `#${cid}`,
          total: shifts.reduce((a, b) => a + b, 0),
        }))
        .sort((a, b) => b.total - a.total)
        .map((c) => c.name);
    },
    companiesSeries() {
      const orderedIds = Object.entries(this.companyAgg)
        .map(([cid, shifts]) => ({ cid, total: shifts.reduce((a, b) => a + b, 0) }))
        .sort((a, b) => b.total - a.total)
        .map((c) => c.cid);
      return this.shiftLabels.map((label, i) => ({
        name: label,
        data: orderedIds.map((cid) => this.companyAgg[cid]?.[i] || 0),
      }));
    },
    companiesOpts() {
      return {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
          fontFamily: "Roboto, sans-serif",
        },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 3, barHeight: "65%" },
        },
        colors: SHIFT_COLORS,
        xaxis: {
          categories: this.companyCategories,
          labels: { style: { fontSize: "12px" } },
        },
        yaxis: { labels: { style: { fontSize: "12px" }, maxWidth: 200 } },
        legend: { position: "bottom", horizontalAlign: "center" },
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false },
        fill: { opacity: 1 },
        grid: { borderColor: "#f1f1f1" },
      };
    },

    // ─── Daily trend ──────────────────────────────────────────────────────────
    hasDates() {
      return this.rawData.some((item) => !!item.date);
    },
    dayAgg() {
      const map = {};
      for (const item of this.rawData) {
        if (!item.date) continue;
        if (!map[item.date]) map[item.date] = [0, 0, 0, 0];
        const s = (item.shift || 1) - 1;
        if (s >= 0 && s < 4) map[item.date][s] += Number(item.quantity || 0);
      }
      return map;
    },
    dayCategories() {
      return Object.keys(this.dayAgg).sort();
    },
    daysSeries() {
      return this.shiftLabels.map((label, i) => ({
        name: label,
        data: this.dayCategories.map((d) => this.dayAgg[d]?.[i] || 0),
      }));
    },
    daysOpts() {
      return {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: true },
          zoom: { enabled: true },
          fontFamily: "Roboto, sans-serif",
        },
        plotOptions: { bar: { horizontal: false, borderRadius: 3, columnWidth: "60%" } },
        colors: SHIFT_COLORS,
        xaxis: {
          categories: this.dayCategories,
          labels: { style: { fontSize: "11px" } },
        },
        legend: { position: "bottom" },
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false },
        fill: { opacity: 1 },
        grid: { borderColor: "#f1f1f1" },
      };
    },
  },
  methods: {
    async fetchData() {
      const restaurantId = this.$store.state.restaurant?.id;
      if (!restaurantId || !this.date?.length) return;
      this.loading = true;
      try {
        const from = this.date[0];
        const to = this.date[1] || this.date[0];
        const [ordersRes, companiesRes] = await Promise.all([
          reportService.getFoodOrdersOrdered(restaurantId, { date: from, date2: to }),
          companyService.getCompanies(),
        ]);
        this.rawData = Array.isArray(ordersRes.data) ? ordersRes.data : [];
        this.companies = Array.isArray(companiesRes.data) ? companiesRes.data : [];
      } catch {
        this.rawData = [];
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    date: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
  },
  created() {
    this.fetchData();
  },
};
</script>
